import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
	Eye,
	ImageIcon,
	Loader2,
	Mail,
	Monitor,
	Smartphone,
	Users,
	X,
} from "lucide-react";
import { createContext, use, useCallback, useId, useState } from "react";
import { BrandInput } from "@/components/brand/brand-input";
import { BrandTextarea } from "@/components/brand/brand-textarea";
import { AD_SLOT_PRICE_USD } from "@/lib/ad-pricing";
import { trackEvent } from "@/lib/analytics";
import { toast } from "@/lib/toast";

/** Mirror server-side logo resolution so the preview matches the live ad. */
function previewLogoUrl(rawUrl: string): string | null {
	const trimmed = rawUrl.trim();
	if (!trimmed) return null;
	try {
		const withProtocol = /^https?:\/\//i.test(trimmed)
			? trimmed
			: `https://${trimmed}`;
		const { hostname } = new URL(withProtocol);
		if (!hostname) return null;
		return `/api/logo?host=${encodeURIComponent(hostname)}`;
	} catch {
		return null;
	}
}

interface AdvertiseDialogContextValue {
	openAdvertiseDialog: (surface?: string) => void;
	closeAdvertiseDialog: () => void;
	isOpen: boolean;
}

const AdvertiseDialogContext =
	createContext<AdvertiseDialogContextValue | null>(null);

export function useAdvertiseDialog() {
	const context = use(AdvertiseDialogContext);
	if (!context) {
		throw new Error(
			"useAdvertiseDialog must be used within an AdvertiseDialogProvider",
		);
	}
	return context;
}

interface AdvertiseDialogProviderProps {
	children: React.ReactNode;
}

export function AdvertiseDialogProvider({
	children,
}: AdvertiseDialogProviderProps) {
	const [isOpen, setIsOpen] = useState(false);

	const openAdvertiseDialog = useCallback((surface = "unknown") => {
		trackEvent("advertise_clicked", { surface });
		setIsOpen(true);
	}, []);

	const closeAdvertiseDialog = useCallback(() => {
		setIsOpen(false);
	}, []);

	return (
		<AdvertiseDialogContext.Provider
			value={{ openAdvertiseDialog, closeAdvertiseDialog, isOpen }}
		>
			{children}
			<AdvertiseDialogContent open={isOpen} onOpenChange={setIsOpen} />
		</AdvertiseDialogContext.Provider>
	);
}

function AdvertiseDialogContent({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [isLoading, setIsLoading] = useState(false);
	const [sponsorName, setSponsorName] = useState("");
	const [sponsorDescription, setSponsorDescription] = useState("");
	const [sponsorWebsiteUrl, setSponsorWebsiteUrl] = useState("");
	const [logoError, setLogoError] = useState(false);
	const nameId = useId();
	const descriptionId = useId();
	const urlId = useId();
	const currentPriceUsd = AD_SLOT_PRICE_USD;
	const previewLogo = previewLogoUrl(sponsorWebsiteUrl);
	const hasPreview =
		sponsorWebsiteUrl.trim().length > 0 && sponsorName.trim().length > 0;
	const canSubmit =
		sponsorName.trim().length > 0 &&
		sponsorDescription.trim().length > 0 &&
		sponsorWebsiteUrl.trim().length > 0;

	const handleCheckout = async () => {
		if (!canSubmit) {
			toast.error("Add your name, description, and website URL first");
			return;
		}
		setIsLoading(true);
		trackEvent("ad_checkout_started", {});
		try {
			const response = await fetch("/api/stripe/ad-checkout-public", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					sponsorName: sponsorName.trim(),
					sponsorDescription: sponsorDescription.trim(),
					sponsorWebsiteUrl: sponsorWebsiteUrl.trim(),
				}),
			});

			const data = (await response.json().catch(() => null)) as {
				error?: string;
				url?: string;
			} | null;

			if (!response.ok) {
				throw new Error(data?.error || "Failed to start checkout");
			}

			if (data?.url) {
				window.location.href = data.url;
			}
		} catch (error) {
			setIsLoading(false);
			toast.error(
				error instanceof Error ? error.message : "Failed to start checkout",
			);
		}
	};

	return (
		<DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
			<DialogPrimitive.Portal>
				<DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
				<DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] px-4 duration-200 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
					<div className="max-h-[90vh] overflow-y-auto border border-border bg-card text-card-foreground">
						<div className="flex items-center justify-between border-b border-border px-5 py-3">
							<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
								&gt; Advertise
							</span>
							<DialogPrimitive.Close className="-mr-2 inline-flex size-10 items-center justify-center font-mono text-sm text-muted-foreground transition-[color,transform] hover:text-brand active:scale-[0.96]">
								<X className="size-3.5" />
								<span className="sr-only">Close</span>
							</DialogPrimitive.Close>
						</div>

						<div className="flex flex-col gap-1.5 px-5">
							<DialogPrimitive.Title className="sr-only">
								Get your startup in front of founders
							</DialogPrimitive.Title>
							<DialogPrimitive.Description className="text-sm leading-snug text-muted-foreground">
								Reach hundreds of founders building with AI agents. One month of
								placement across the sidebar, mobile banner, and newsletter.
							</DialogPrimitive.Description>
						</div>

						<div className="mt-4 flex flex-col gap-3 px-5">
							<label className="flex flex-col gap-1.5" htmlFor={urlId}>
								<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
									Website URL
								</span>
								<BrandInput
									id={urlId}
									name="sponsorWebsiteUrl"
									placeholder="example.com"
									value={sponsorWebsiteUrl}
									onChange={(event) => {
										setSponsorWebsiteUrl(event.target.value);
										setLogoError(false);
									}}
									required
									disabled={isLoading}
								/>
								<span className="font-mono text-xs text-muted-foreground">
									We pull your logo automatically from this URL.
								</span>
							</label>
							<label className="flex flex-col gap-1.5" htmlFor={nameId}>
								<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
									Title
								</span>
								<BrandInput
									id={nameId}
									name="sponsorName"
									placeholder="Your product name"
									value={sponsorName}
									onChange={(event) => setSponsorName(event.target.value)}
									maxLength={80}
									required
									disabled={isLoading}
								/>
							</label>
							<label className="flex flex-col gap-1.5" htmlFor={descriptionId}>
								<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
									Description
								</span>
								<BrandTextarea
									id={descriptionId}
									name="sponsorDescription"
									placeholder="Tagline shown in your ad"
									value={sponsorDescription}
									onChange={(event) =>
										setSponsorDescription(event.target.value)
									}
									maxLength={180}
									required
									disabled={isLoading}
								/>
							</label>

							<div className="flex flex-col gap-1.5">
								<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
									Desktop preview
								</span>
								<div className="border border-border bg-muted/30 p-3">
									{hasPreview ? (
										<div className="flex flex-col items-center justify-center gap-3 border border-border bg-card p-4 text-center">
											<div className="flex w-full items-center justify-between">
												<span className="font-mono text-sm uppercase tracking-[0.14em] text-foreground">
													&gt; Sponsored
												</span>
											</div>
											{previewLogo && !logoError ? (
												<img
													src={previewLogo}
													alt=""
													className="size-12 object-contain"
													loading="lazy"
													decoding="async"
													onError={() => setLogoError(true)}
												/>
											) : (
												<div className="flex size-12 items-center justify-center border border-border bg-muted font-mono text-xl text-foreground">
													{sponsorName.charAt(0).toUpperCase()}
												</div>
											)}
											<div className="flex flex-col gap-1">
												{sponsorName ? (
													<span className="font-mono text-sm uppercase tracking-[0.08em] text-foreground leading-tight">
														{sponsorName}
													</span>
												) : null}
												{sponsorDescription ? (
													<span className="line-clamp-2 text-sm leading-snug text-muted-foreground">
														{sponsorDescription}
													</span>
												) : null}
											</div>
										</div>
									) : (
										<div className="flex flex-col items-center justify-center gap-3 border border-dashed border-border bg-card p-8 text-center">
											<div className="flex size-10 items-center justify-center border border-dashed border-border bg-muted/40">
												<ImageIcon className="size-5 text-muted-foreground/40" />
											</div>
											<div className="flex flex-col gap-1">
												<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground/70">
													Live preview
												</span>
												<span className="font-mono text-xs text-muted-foreground/50">
													Add a URL and name to see your ad
												</span>
											</div>
										</div>
									)}
								</div>
							</div>

							<div className="grid grid-cols-2 gap-px border border-border bg-border">
								<div className="flex flex-col gap-1 bg-card px-4 py-3">
									<Users className="size-3.5 text-muted-foreground" />
									<span className="font-mono font-normal text-base text-foreground">
										Founders
									</span>
									<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
										AI agent builders
									</span>
								</div>
								<div className="flex flex-col gap-1 bg-card px-4 py-3">
									<Eye className="size-3.5 text-muted-foreground" />
									<span className="font-mono font-normal text-base text-foreground">
										Targeted
									</span>
									<span className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
										Agent-native audience
									</span>
								</div>
							</div>

							<div className="mt-1 flex items-center justify-between gap-4 border-t border-border pt-3">
								<p className="font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
									1 month placement
								</p>
								<p className="font-mono text-2xl tabular-nums text-foreground">
									${currentPriceUsd}
									<span className="ml-1 font-mono text-sm text-muted-foreground">
										USD
									</span>
								</p>
							</div>
						</div>

						<div className="px-5 pt-4 pb-5">
							<button
								type="button"
								disabled={isLoading || !canSubmit}
								onClick={handleCheckout}
								className="inline-flex w-full min-h-11 items-center justify-center gap-2 bg-brand px-5 font-mono text-sm uppercase tracking-[0.14em] text-background transition-[background-color,transform] hover:bg-brand-hover active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40"
							>
								{isLoading ? (
									<>
										<Loader2 className="size-3.5 animate-spin" />
										Redirecting
									</>
								) : (
									"Proceed to payment"
								)}
							</button>

							<div className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 font-mono text-sm uppercase tracking-[0.12em] text-muted-foreground">
								<span className="inline-flex items-center gap-1.5">
									<Monitor className="size-3.5" />
									Desktop sidebar
								</span>
								<span className="inline-flex items-center gap-1.5">
									<Smartphone className="size-3.5" />
									Mobile banner
								</span>
								<span className="inline-flex items-center gap-1.5">
									<Mail className="size-3.5" />
									Newsletter
								</span>
							</div>
						</div>
					</div>
				</DialogPrimitive.Content>
			</DialogPrimitive.Portal>
		</DialogPrimitive.Root>
	);
}
